import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, IconButton, Chip,
  TextField, InputAdornment, Avatar, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, MenuItem, CircularProgress, Tooltip,
  FormControlLabel, Alert, Switch, Divider, Badge
} from '@mui/material';
import {
  Add, Edit, Delete, Search, PersonAdd, LockReset, AccountCircle,
  CheckCircle as ActiveIcon, Cancel as InactiveIcon, Key as KeyIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { teachersAPI, authAPI, settingsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const STATUS = ['active', 'inactive', 'resigned', 'retired'];
const EMP_TYPE = ['full_time', 'part_time', 'contract'];
const GENDER = ['male', 'female', 'other'];

const EMPTY = {
  first_name:'', last_name:'', other_names:'', gender:'male', date_of_birth:'',
  employee_number:'', date_joined:'', phone:'', email:'', address:'',
  qualification:'', specialization:'', employment_type:'full_time',
  salary:'', status:'active'
};

export default function Teachers() {
  const { enqueueSnackbar } = useSnackbar();
  const { can } = useAuth();
  const [teachers, setTeachers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Teacher form dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const [createAccount, setCreateAccount] = useState(true);
  const [accountUsername, setAccountUsername] = useState('');
  const [accountPassword, setAccountPassword] = useState('123456');
  const [accountRole, setAccountRole] = useState('teacher');
  const [availableRoles, setAvailableRoles] = useState(['teacher','class_teacher','registrar','viewer']);

  // Account management dialog
  const [accountDialog, setAccountDialog] = useState(false);
  const [accountTeacher, setAccountTeacher] = useState(null);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('123456');
  const [accountSaving, setAccountSaving] = useState(false);

  // Reset password dialog
  const [resetDialog, setResetDialog] = useState(false);
  const [resetUserId, setResetUserId] = useState(null);
  const [resetPassword, setResetPasswordVal] = useState('123456');
  const [resetSaving, setResetSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await teachersAPI.getAll({ search, page: page + 1, limit: rowsPerPage });
      if (res.data.success) { setTeachers(res.data.data); setTotal(res.data.total); }
    } finally { setLoading(false); }
  }, [search, page, rowsPerPage]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
  }, []);

  const openCreate = () => {
    setEditing(null); setForm(EMPTY); setPhoto(null);
    setCreateAccount(true);
    setAccountRole('teacher');
    setAccountUsername(''); setAccountPassword('123456');
    setDialogOpen(true);
  };

  const openEdit = async (teacher) => {
    setEditing(teacher);
    setForm({
      first_name: teacher.first_name || '', last_name: teacher.last_name || '',
      other_names: teacher.other_names || '', gender: teacher.gender || 'male',
      date_of_birth: teacher.date_of_birth?.split('T')[0] || '',
      employee_number: teacher.employee_number || '',
      date_joined: teacher.date_joined?.split('T')[0] || '',
      phone: teacher.phone || '', email: teacher.email || '',
      address: teacher.address || '', qualification: teacher.qualification || '',
      specialization: teacher.specialization || '',
      employment_type: teacher.employment_type || 'full_time',
      salary: teacher.salary || '', status: teacher.status || 'active'
    });
    setPhoto(null); setCreateAccount(false);
    try {
      const res = await teachersAPI.getById(teacher.id);
    } catch {}
    setDialogOpen(true);
  };

  // Auto-generate username from name
  const generateUsername = (first, last) => {
    return `${first}.${last}`.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9.]/g, '');
  };

  const handleSave = async () => {
    if (!form.first_name || !form.last_name || !form.employee_number) {
      return enqueueSnackbar('First name, last name and employee number are required', { variant: 'warning' });
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.keys(form).forEach(k => fd.append(k, form[k]));
      fd.append('create_account', createAccount ? 'true' : 'false');
      if (createAccount && !editing) {
        fd.append('username', accountUsername || generateUsername(form.first_name, form.last_name));
        fd.append('password', accountPassword || '123456');
        fd.append('account_role', accountRole || 'teacher');
      }
      if (photo) fd.append('profile_photo', photo);

      let res;
      if (editing) res = await teachersAPI.update(editing.id, fd);
      else res = await teachersAPI.create(fd);

      enqueueSnackbar(res.data.message || (editing ? 'Teacher updated' : 'Teacher created'), { variant: 'success' });
      setDialogOpen(false);
      load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Error saving teacher', { variant: 'error' });
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this teacher? Their login account will be unlinked.')) return;
    try {
      await teachersAPI.delete(id);
      enqueueSnackbar('Teacher deleted', { variant: 'success' });
      load();
    } catch { enqueueSnackbar('Error deleting teacher', { variant: 'error' }); }
  };

  const openCreateAccount = (teacher) => {
    setAccountTeacher(teacher);
    setNewUsername(generateUsername(teacher.first_name, teacher.last_name));
    setNewPassword('123456');
    setAccountDialog(true);
  };

  const handleCreateAccount = async () => {
    setAccountSaving(true);
    try {
      const res = await teachersAPI.createAccount(accountTeacher.id, { username: newUsername, password: newPassword });
      enqueueSnackbar(res.data.message, { variant: 'success' });
      setAccountDialog(false);
      load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Error creating account', { variant: 'error' });
    } finally { setAccountSaving(false); }
  };

  const openResetPassword = (userId) => {
    setResetUserId(userId);
    setResetPasswordVal('123456');
    setResetDialog(true);
  };

  const handleResetPassword = async () => {
    setResetSaving(true);
    try {
      const res = await authAPI.resetPassword(resetUserId, { new_password: resetPassword });
      enqueueSnackbar(res.data.message, { variant: 'success' });
      setResetDialog(false);
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Error resetting password', { variant: 'error' });
    } finally { setResetSaving(false); }
  };

  const handleToggleAccount = async (userId) => {
    try {
      await authAPI.toggleUser(userId);
      enqueueSnackbar('Account status updated', { variant: 'success' });
      load();
    } catch { enqueueSnackbar('Error updating account', { variant: 'error' }); }
  };

  const getPhotoUrl = (f) => f ? `${process.env.REACT_APP_API_URL?.replace('/api', '')}/uploads/${f}` : null;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" fontWeight={700}>Teachers Management</Typography>
        {can('teachers', 'full') && <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Add Teacher</Button>}
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: '12px !important' }}>
          <TextField placeholder="Search by name, employee number..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            sx={{ maxWidth: 400 }} />
        </CardContent>
      </Card>

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell>Photo</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Employee No.</TableCell>
                <TableCell>Subjects</TableCell>
                <TableCell>Login Account</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6 }}><CircularProgress /></TableCell></TableRow>
                : teachers.length === 0
                ? <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>No teachers found</TableCell></TableRow>
                : teachers.map(t => (
                  <TableRow key={t.id} hover>
                    <TableCell>
                      <Avatar src={getPhotoUrl(t.profile_photo)} sx={{ width: 38, height: 38, bgcolor: 'secondary.main', fontSize: 13 }}>
                        {t.first_name?.[0]}{t.last_name?.[0]}
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={600} fontSize={14}>{t.first_name} {t.last_name}</Typography>
                      {t.qualification && <Typography variant="caption" color="text.secondary">{t.qualification}</Typography>}
                    </TableCell>
                    <TableCell><Chip label={t.employee_number} size="small" variant="outlined" /></TableCell>
                    <TableCell>
                      <Typography fontSize={12} color="text.secondary" sx={{ maxWidth: 180 }} noWrap>{t.subjects || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      {t.username ? (
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AccountCircle fontSize="small" color="primary" />
                            <Typography fontSize={13} fontWeight={600}>{t.username}</Typography>
                            <Chip
                              label={t.account_active ? 'Active' : 'Disabled'}
                              size="small"
                              color={t.account_active ? 'success' : 'default'}
                              sx={{ ml: 0.5 }}
                            />
                          </Box>
                          {t.must_change_password ? (
                            <Chip label="Must change password" size="small" color="warning" sx={{ mt: 0.3, fontSize: 10 }} />
                          ) : t.last_login ? (
                            <Typography fontSize={10} color="text.secondary">
                              Last login: {new Date(t.last_login).toLocaleDateString()}
                            </Typography>
                          ) : (
                            <Typography fontSize={10} color="text.secondary">Never logged in</Typography>
                          )}
                        </Box>
                      ) : (
                        <Chip label="No account" size="small" color="default" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={t.status} size="small" color={t.status === 'active' ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell align="right">
                      {can('teachers', 'full') && (
                        <>
                          <Tooltip title="Edit Teacher">
                            <IconButton size="small" onClick={() => openEdit(t)}><Edit fontSize="small" /></IconButton>
                          </Tooltip>
                          {!t.username ? (
                            <Tooltip title="Create Login Account">
                              <IconButton size="small" color="primary" onClick={() => openCreateAccount(t)}>
                                <PersonAdd fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <>
                              <Tooltip title="Reset Password">
                                <IconButton size="small" color="warning" onClick={() => openResetPassword(t.user_id)}>
                                  <KeyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t.account_active ? 'Disable Account' : 'Enable Account'}>
                                <IconButton size="small" color={t.account_active ? 'error' : 'success'}
                                  onClick={() => handleToggleAccount(t.user_id)}>
                                  {t.account_active ? <InactiveIcon fontSize="small" /> : <ActiveIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          <Tooltip title="Delete Teacher">
                            <IconButton size="small" color="error" onClick={() => handleDelete(t.id)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={total} page={page} rowsPerPage={rowsPerPage}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]} />
      </Card>

      {/* ── Teacher Form Dialog ── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit Teacher' : 'Add Teacher'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={6}><TextField label="First Name *" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Last Name *" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} /></Grid>
            <Grid item xs={12} sm={4}>
              <TextField select label="Gender" value={form.gender} onChange={e => setForm({ ...form, gender: e.target.value })}>
                {GENDER.map(g => <MenuItem key={g} value={g} sx={{ textTransform: 'capitalize' }}>{g}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}><TextField label="Employee Number *" value={form.employee_number} onChange={e => setForm({ ...form, employee_number: e.target.value })} /></Grid>
            <Grid item xs={12} sm={4}><TextField label="Phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Status" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                {STATUS.map(s => <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>)}
              </TextField>
            </Grid>

            {/* Login account section - only when creating new teacher */}
            {!editing && (
              <>
                <Grid item xs={12}><Divider /></Grid>
                <Grid item xs={12}>
                  <FormControlLabel control={
                    <Switch checked={createAccount} onChange={e => setCreateAccount(e.target.checked)} color="primary" />
                  } label={<Typography fontWeight={600}>Create Login Account for this Teacher</Typography>} />
                  {createAccount && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      The teacher will be required to change their password on first login.
                    </Alert>
                  )}
                </Grid>
                {createAccount && (
                  <>
                    <Grid item xs={12} sm={4}>
                      <TextField label="Username" value={accountUsername}
                        onChange={e => setAccountUsername(e.target.value)}
                        placeholder={form.first_name && form.last_name ? generateUsername(form.first_name, form.last_name) : 'firstname.lastname'}
                        helperText="Leave blank to auto-generate from name" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="Initial Password" value={accountPassword}
                        onChange={e => setAccountPassword(e.target.value)}
                        helperText="Teacher must change this on first login" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField select label="System Role" value={accountRole}
                        onChange={e => setAccountRole(e.target.value)}
                        helperText="What this user can access">
                        {availableRoles.map(r => (
                          <MenuItem key={r} value={r} sx={{ textTransform: 'capitalize' }}>
                            {r.replace(/_/g, ' ')}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </>
                )}
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Create Account for Existing Teacher ── */}
      <Dialog open={accountDialog} onClose={() => setAccountDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle><PersonAdd sx={{ mr: 1, verticalAlign: 'middle' }} />Create Login Account</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Creating an account for <strong>{accountTeacher?.first_name} {accountTeacher?.last_name}</strong>.
            They will be required to change the password on first login.
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField label="Username" value={newUsername} onChange={e => setNewUsername(e.target.value)} fullWidth size="small" />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Initial Password" value={newPassword} onChange={e => setNewPassword(e.target.value)} fullWidth size="small" helperText="Minimum 6 characters" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccountDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateAccount} disabled={accountSaving}>
            {accountSaving ? <CircularProgress size={20} /> : 'Create Account'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Reset Password ── */}
      <Dialog open={resetDialog} onClose={() => setResetDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle><KeyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />Reset Password</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            The teacher will be required to change this password on next login.
          </Alert>
          <TextField label="New Password" value={resetPassword} onChange={e => setResetPasswordVal(e.target.value)}
            fullWidth size="small" helperText="Minimum 6 characters" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialog(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleResetPassword} disabled={resetSaving}>
            {resetSaving ? <CircularProgress size={20} /> : 'Reset Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
